import { Controller } from "@hotwired/stimulus";

// Persistent "now playing" bar for the BGM library — filtering/paginating
// hides a track's card but must not silently keep it playing with no way to
// see or reach it. Instead of pausing on hide, surface what's playing and
// let the user stop it from anywhere on the page; only real navigation
// (which tears the audio-player controller down) actually stops playback.
export default class extends Controller {
  static targets = ["name"];

  #activeElement = null;

  connect() {
    this._onPlaying = (e) => this.#show(e.detail);
    this._onStopped = (e) => this.#hideIfActive(e.detail.element);
    document.addEventListener("audio-player:now-playing", this._onPlaying);
    document.addEventListener("audio-player:stopped", this._onStopped);
  }

  disconnect() {
    document.removeEventListener("audio-player:now-playing", this._onPlaying);
    document.removeEventListener("audio-player:stopped", this._onStopped);
  }

  stop() {
    if (!this.#activeElement) return;
    this.application.getControllerForElementAndIdentifier(this.#activeElement, "audio-player")?.pause();
  }

  jumpToTrack() {
    if (!this.#activeElement) return;
    this.#activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
    this.#activeElement.classList.add("track-card--flash");
    setTimeout(() => this.#activeElement.classList.remove("track-card--flash"), 900);
  }

  #show({ name, element }) {
    this.#activeElement = element;
    if (this.hasNameTarget) this.nameTarget.textContent = name;
    // [hidden] is display:none, which can't transition — remove it, then add
    // the visible class next frame so the slide-in actually animates.
    this.element.hidden = false;
    requestAnimationFrame(() => this.element.classList.add("now-playing-bar--visible"));
  }

  #hideIfActive(element) {
    if (element !== this.#activeElement) return;
    this.#activeElement = null;
    this.element.classList.remove("now-playing-bar--visible");
    const duration = parseFloat(getComputedStyle(this.element).transitionDuration) * 1000;
    if (duration > 0) {
      this.element.addEventListener("transitionend", () => { this.element.hidden = true; }, { once: true });
    } else {
      this.element.hidden = true;
    }
  }
}
